import { Inject, Injectable, Optional } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { Observable } from 'rxjs';
import {
  navigationItemIdQueryParam,
  openAppQueryParam
} from '@/app/platform/platform-core/user/constants';
import { USER_QUERIES, UserQueries } from '@/app/platform/platform-core/user/store/types';
import { LAYOUT_ACTIONS, LayoutActions } from '@core/store/types';
import { UserEgiApp } from '@/app/platform/platform-core/user/models/user-egi-app';
import { NGXLogger } from 'ngx-logger';
import { UserStore } from '@/app/platform/platform-core/user/store/user.store';
import { AUTO_RESET_AFTER_LOGOUT, ResettableStore } from '@core/guards/clear-stores-after-logout.guard';
import { EGI_APP_ID_ROUTE_PARAM } from '../../../../projects/page-framework/src/lib/constants';

@Injectable({
  providedIn: 'root'
})
export class OpenAppGuard  {
  constructor(
    private readonly router: Router,
    @Inject(USER_QUERIES)
    private readonly userQueries: UserQueries,
    @Inject(LAYOUT_ACTIONS)
    private readonly layoutActions: LayoutActions,
    private readonly logger: NGXLogger,
    private readonly userStore: UserStore,
    @Optional()
    @Inject(AUTO_RESET_AFTER_LOGOUT)
    private readonly stores?: ResettableStore[]
  ) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    return this.handle(route, state);
  }

  canActivateChild(
    childRoute: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    return this.handle(childRoute, state);
  }

  private handle(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    if (!route.queryParamMap.get(openAppQueryParam)) {
      return true;
    }

    (async () => {
      this.resetAllConfiguredStores();
    })();

    const appId = route.paramMap.get(EGI_APP_ID_ROUTE_PARAM);
    const app = this.userQueries
      .getAvailableApps()
      .find(a => a.id === appId)
      .clone();


    if (app.rootNavigationItem.isGenericAppIndex) {
      if (!app.rootNavigationItem.hasDirectlyNavigableDescendants) {
        this.logger.info(
          'Generic index and no children -> going to index page without opening sidebar'
        );
        return this.router.createUrlTree([app.rootNavigationItem.routerLink], {
          queryParams: {
            [navigationItemIdQueryParam]: app.rootNavigationItem.id
          }
        });
      }
      if (app.rootNavigationItem.hasExactlyOneNavigableDescendant) {
        this.logger.info(
          'Generic index and 1 child -> going to first child: ' +
            app.rootNavigationItem.firstNavigableDescendant.name
        );
        return this.routeToFirstDirectlyNavigableDescendant(app);
      }
      this.logger.info('Generic index and multiple children -> opening index and show sidebar');
      this.layoutActions.openSideBar();
    }

    return this.router.createUrlTree([app.rootNavigationItem.routerLink], {
      queryParams: {
        [navigationItemIdQueryParam]: app.rootNavigationItem.id
      }
    });
  }

  private routeToFirstDirectlyNavigableDescendant(app: UserEgiApp): UrlTree {
    const onlyNavigableChild = app.rootNavigationItem.firstNavigableDescendant;
    return this.router.createUrlTree([onlyNavigableChild.routerLink], {
      queryParams: {
        [navigationItemIdQueryParam]: onlyNavigableChild.id
      }
    });
  }

  private resetAllConfiguredStores(): void {
    if (!this.stores) {
      this.logger.info(
        'No stores configured to reset! Use AutoResetAfterLogoutProvider to reset the stores after a user has changed'
      );
    }
    this.stores?.forEach(async s => {
      s.reset();
    });
    this.logger.info('Stores reset after app change');
  }
}
